import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
interface ScannedProduct {
  title: string;
  editionLabel: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl: string;
}
const WOMEN_NAMES = ['Mary', 'Patricia', 'Linda', 'Barbara', 'Elizabeth', 'Susan', 'Jessica', 'Sarah', 'Karen', 'Lisa', 'Nancy', 'Betty', 'Margaret', 'Sandra', 'Ashley', 'Dorothy', 'Kimberly', 'Emily', 'Donna', 'Michelle', 'Carol', 'Amanda', 'Melissa', 'Deborah', 'Stephanie', 'Rebecca', 'Sharon', 'Laura', 'Cynthia', 'Kathleen', 'Amy', 'Angela', 'Shirley', 'Anna', 'Brenda', 'Pamela', 'Emma', 'Nicole', 'Helen', 'Samantha', 'Katherine', 'Christine', 'Debra', 'Rachel', 'Carolyn', 'Janet', 'Catherine', 'Maria', 'Heather', 'Diane', 'Ruth', 'Julie', 'Olivia', 'Joyce', 'Virginia', 'Victoria', 'Kelly', 'Lauren', 'Christina', 'Joan', 'Evelyn', 'Judith', 'Andrea', 'Hannah', 'Megan', 'Cheryl', 'Jacqueline', 'Martha', 'Gloria', 'Teresa', 'Ann', 'Sara', 'Madison', 'Frances', 'Kathryn', 'Janice', 'Jean', 'Abigail', 'Alice', 'Judy', 'Sophia', 'Grace', 'Denise', 'Amber', 'Doris', 'Marilyn', 'Danielle', 'Beverly', 'Isabella', 'Theresa', 'Diana', 'Natalie', 'Brittany', 'Charlotte', 'Marie', 'Kayla', 'Alexis', 'Lori', 'Tammy', 'Tracy', 'Bonnie', 'Connie', 'Tiffany', 'Wendy', 'Robin', 'Peggy'];
const US_CITIES = ['Miami, FL', 'Houston, TX', 'Los Angeles, CA', 'New York, NY', 'Phoenix, AZ', 'San Antonio, TX', 'Dallas, TX', 'San Diego, CA', 'Chicago, IL', 'Austin, TX', 'Jacksonville, FL', 'San Jose, CA', 'Fort Worth, TX', 'Columbus, OH', 'Charlotte, NC', 'Indianapolis, IN', 'San Francisco, CA', 'Seattle, WA', 'Denver, CO', 'Nashville, TN', 'El Paso, TX', 'Detroit, MI', 'Portland, OR', 'Las Vegas, NV', 'Memphis, TN', 'Louisville, KY', 'Baltimore, MD', 'Milwaukee, WI', 'Albuquerque, NM', 'Tucson, AZ', 'Fresno, CA', 'Sacramento, CA', 'Atlanta, GA', 'Tampa, FL', 'Orlando, FL', 'Raleigh, NC', 'Omaha, NE', 'Cleveland, OH', 'Tulsa, OK', 'Oakland, CA', 'Minneapolis, MN', 'Arlington, TX', 'Bakersfield, CA', 'Aurora, CO', 'Honolulu, HI', 'Santa Ana, CA', 'Riverside, CA', 'Corpus Christi, TX', 'Boise, ID', 'Scottsdale, AZ'];
const FALLBACK_PRODUCTS: ScannedProduct[] = [{
  title: 'The Silent Fire',
  editionLabel: 'Digital Edition',
  priceNumber: 7,
  priceText: '$7',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/2213CaeySf7N9C7zA9Vc4h/mokou_El_Fuego_que_Nadie_Ve_1.png'
}];
function scanPageForProducts(): ScannedProduct[] {
  const products: ScannedProduct[] = [];
  try {
    // Strategy 1: Look for JSON-LD structured data
    const jsonLdScripts = document.querySelectorAll('script[type="application/ld+json"]');
    jsonLdScripts.forEach(script => {
      try {
        const data = JSON.parse(script.textContent || '');
        if (data['@type'] === 'Product' && data.offers) {
          const offer = Array.isArray(data.offers) ? data.offers[0] : data.offers;
          products.push({
            title: data.name || 'Product',
            editionLabel: 'Digital Edition',
            priceNumber: parseFloat(offer.price) || 0,
            priceText: `$${offer.price}`,
            thumbnailUrl: data.image || ''
          });
        }
      } catch {}
    });
    if (products.length > 0) return products;
    // Strategy 2: Scan DOM for price patterns and nearby product info
    const bodyText = document.body.innerText || '';
    const priceMatches = bodyText.match(/\$\s?\d+(\.\d{2})?/g);
    // Look for the ebook image
    const images = document.querySelectorAll('img');
    let thumbnailUrl = '';
    images.forEach(img => {
      const src = img.getAttribute('src') || '';
      if (src.includes('Fuego') || src.includes('Silent') || src.includes('ebook') || src.includes('mokou')) {
        thumbnailUrl = src;
      }
    });
    // Look for product title patterns
    const headings = document.querySelectorAll('h1, h2, h3, h4');
    let productTitle = '';
    headings.forEach(h => {
      const text = h.textContent || '';
      if (text.includes('SILENT FIRE') || text.includes('Silent Fire')) {
        productTitle = 'The Silent Fire';
      }
    });
    // Look for the sale price ($7)
    if (priceMatches) {
      const smallestPrice = priceMatches.map(p => parseFloat(p.replace(/[^0-9.]/g, ''))).filter(p => p > 0 && p < 50).sort((a, b) => a - b)[0];
      if (smallestPrice && productTitle) {
        products.push({
          title: productTitle,
          editionLabel: 'Complete Manual — Digital Edition',
          priceNumber: smallestPrice,
          priceText: `$${smallestPrice}`,
          thumbnailUrl: thumbnailUrl || FALLBACK_PRODUCTS[0].thumbnailUrl
        });
      }
    }
  } catch {}
  return products.length > 0 ? products : FALLBACK_PRODUCTS;
}
function getRandomItem<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function getRandomInterval(): number {
  return Math.floor(Math.random() * (25000 - 15000 + 1)) + 15000;
}
interface Notification {
  name: string;
  city: string;
  product: ScannedProduct;
  timeAgo: string;
}
const TIME_AGOS = ['just now', '1 min ago', '2 min ago', '3 min ago', '5 min ago', '8 min ago', '10 min ago', '12 min ago', '15 min ago', '20 min ago'];
export function RecentPurchaseToast() {
  const [notification, setNotification] = useState<Notification | null>(null);
  const [visible, setVisible] = useState(false);
  const productsRef = useRef<ScannedProduct[]>([]);
  const nameHistoryRef = useRef<string[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const getUniqueName = useCallback((): string => {
    let name = getRandomItem(WOMEN_NAMES);
    let attempts = 0;
    while (nameHistoryRef.current.includes(name) && attempts < 20) {
      name = getRandomItem(WOMEN_NAMES);
      attempts++;
    }
    nameHistoryRef.current.push(name);
    if (nameHistoryRef.current.length > 10) {
      nameHistoryRef.current.shift();
    }
    return name;
  }, []);
  const showNotification = useCallback(() => {
    const product = getRandomItem(productsRef.current);
    const name = getUniqueName();
    const city = getRandomItem(US_CITIES);
    const timeAgo = getRandomItem(TIME_AGOS);
    setNotification({
      name,
      city,
      product,
      timeAgo
    });
    setVisible(true);
    // Hide after 4 seconds
    setTimeout(() => {
      setVisible(false);
    }, 4000);
    // Schedule next notification
    timerRef.current = setTimeout(() => {
      showNotification();
    }, getRandomInterval());
  }, [getUniqueName]);
  useEffect(() => {
    // Scan products on mount
    const scanned = scanPageForProducts();
    productsRef.current = scanned.length > 0 ? scanned : FALLBACK_PRODUCTS;
    // First notification after 3 seconds
    timerRef.current = setTimeout(() => {
      showNotification();
    }, 3000);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [showNotification]);
  return <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-sm px-4" data-id="element-193">
      <AnimatePresence data-id="element-194">
        {visible && notification && <motion.div initial={{
        y: 80,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 80,
        opacity: 0
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} className="bg-white rounded-xl shadow-2xl border border-gray-100 p-4 flex gap-4 items-center" data-id="element-195">
            {/* Thumbnail */}
            <div className="flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-cream" data-id="element-196">
              <img src={notification.product.thumbnailUrl} alt={notification.product.title} className="w-full h-full object-cover" data-id="element-197" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0" data-id="element-198">
              <p className="text-sm text-gray-800 leading-snug" data-id="element-199">
                <span className="font-bold" data-id="element-200">{notification.name}</span>
                <span className="text-gray-500" data-id="element-201"> from </span>
                <span className="font-semibold" data-id="element-202">{notification.city}</span>
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-203">
                purchased{' '}
                <span className="font-semibold text-forest-green" data-id="element-204">
                  {notification.product.title}
                </span>
              </p>
              <div className="flex items-center gap-2 mt-1" data-id="element-205">
                <span className="text-xs font-bold text-forest-green" data-id="element-206">
                  {notification.product.priceText}
                </span>
                <span className="text-xs text-gray-400" data-id="element-207">•</span>
                <span className="text-xs text-gray-400" data-id="element-208">
                  {notification.timeAgo}
                </span>
              </div>
            </div>

            {/* Live indicator */}
            <div className="flex-shrink-0 flex items-center gap-1" data-id="element-209">
              <span className="relative flex h-2.5 w-2.5" data-id="element-210">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" data-id="element-211"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" data-id="element-212"></span>
              </span>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}